import React from "react";
import { AvatarSquare } from "./avatars";
import { Pill } from "./pills";

// =====================================================================
// Club Radar V2 — attribution primitives (DESIGN-SPEC §4 item 12 +
// SOURCE column rule). Attribution is a CONTRACT (brief content
// constraints): the "Recommended by …" lines render wherever built —
// restyled per artboard, never removed. All text arrives via props from
// the existing BE-supplied origin/recommender data.
// =====================================================================

// Artboard color rule for the SOURCE top line: `#1B6FE0` when the
// origin starts with "Recommended", else `#4B5563`. Exposed so callers
// can derive the flag from the BE string when no explicit prop exists.
export const originIsRecommended = (top) => /^recommended/i.test(String(top || ""));

// ── SOURCE lines (table SOURCE column 1a/1b; mobile origin line 1e) ──
// top: 12px/600, nowrap-ellipsis; sub: 11px `#9CA3AF`, mt 2, ellipsis.
// `recommended` overrides the string-derived color when provided.
export const SourceLines = ({ top, sub, recommended, mobile = false, style }) => {
  const isRec = recommended != null ? recommended : originIsRecommended(top);
  const topColor = isRec ? "#1B6FE0" : "#4B5563";
  if (mobile) {
    // 1e origin line: one ellipsised line — top 11px/600 + " · sub" muted.
    return (
      <div
        style={{
          fontSize: 11,
          color: topColor,
          fontWeight: 600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minWidth: 0,
          ...style,
        }}
      >
        {top}
        {sub ? <span style={{ color: "#9CA3AF", fontWeight: 400 }}> · {sub}</span> : null}
      </div>
    );
  }
  return (
    <div style={{ minWidth: 0, ...style }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: topColor,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {top}
      </div>
      {sub ? (
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            color: "#9CA3AF",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {sub}
        </div>
      ) : null}
    </div>
  );
};

// ── Recommender banner (1j desktop / 1k mobile) — §4 item 12 ─────────
// bg `#E8F1FF`, border 1px `#CFE3FF`, r12. Desktop: avatar 26 r8 white/
// `#1B6FE0`, title 12.5/700 + white "Agent" pill, sub 11px at .75
// opacity, white bordered action button. Mobile: pad `10px 12px`, title
// 12/700, sub row with the action beside it, no Agent pill.
// `action` is the fully wired "Contact recommender"/"Contact" button
// node from the caller (existing handler); omit to hide (e.g. when
// `contact_available === false`).
export const RecommenderBanner = ({
  avatarInitials,
  avatarImageUrl,
  title,
  agentPillLabel,
  sub,
  action,
  mobile = false,
  style,
}) => {
  const avatar = (
    <AvatarSquare
      initials={avatarInitials}
      imageUrl={avatarImageUrl}
      size={26}
      radius={8}
      fontSize={9}
      fontWeight={800}
      bg="#fff"
      fg="#1B6FE0"
    />
  );
  if (mobile) {
    return (
      <div
        style={{
          background: "#E8F1FF",
          border: "1px solid #CFE3FF",
          borderRadius: 12,
          padding: "10px 12px",
          ...style,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1B6FE0" }}>{title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 10.5,
              color: "#1B6FE0",
              opacity: 0.75,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {sub}
          </div>
          {action}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#E8F1FF",
        border: "1px solid #CFE3FF",
        borderRadius: 12,
        padding: "11px 13px",
        ...style,
      }}
    >
      {avatar}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#1B6FE0",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </span>
          {agentPillLabel ? (
            <Pill bg="#fff" color="#1B6FE0" pad="1px 6px" fontSize={9.5} fontWeight={700}>
              {agentPillLabel}
            </Pill>
          ) : null}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            color: "#1B6FE0",
            opacity: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sub}
        </div>
      </div>
      {action}
    </div>
  );
};

// White-on-tint banner action button ("Contact recommender" h30 desktop
// / "Contact" h28 mobile): bg `#fff`, border `#CFE3FF`, fg `#1B6FE0`.
export const BannerActionButton = ({ height = 30, radius = 9, fontSize = 11.5, style, children, ...rest }) => (
  <button
    type="button"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      height,
      padding: "0 11px",
      boxSizing: "border-box",
      background: "#fff",
      border: "1px solid #CFE3FF",
      borderRadius: radius,
      color: "#1B6FE0",
      fontSize,
      fontWeight: 700,
      whiteSpace: "nowrap",
      cursor: "pointer",
      fontFamily: "inherit",
      flex: "none",
      ...style,
    }}
    {...rest}
  >
    {children}
  </button>
);

// "by <name>" / "Set by …" attribution caption (status pills, detail).
export const AttributionCaption = ({ fontSize = 10.5, style, children, ...rest }) => (
  <div style={{ fontSize, color: "#9CA3AF", ...style }} {...rest}>
    {children}
  </div>
);
